import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, Heading, Paragraph, Skeleton, Tag } from '@digdir/designsystemet-react';
import { Buildings2Icon, ClockIcon, FilesIcon, FileTextIcon, PencilWritingIcon, PersonGroupIcon, TrashIcon } from '@navikt/aksel-icons';
import { useGetCustomSurveyTemplateByIdQuery } from '../../hooks/queries/useGetCustomSurveyTemplateByIdQuery';
import { useCheckCustomSurveyTemplateUsageQuery } from '../../hooks/queries/useCheckCustomSurveyTemplateUsageQuery';
import { useDeleteCustomSurveyTemplateMutation } from '../../hooks/mutations/useDeleteCustomSurveyTemplateMutation';
import { useDuplicateCustomSurveyTemplateMutation } from '../../hooks/mutations/useDuplicateCustomSurveyTemplateMutation';
import { useArchiveCustomSurveyTemplateMutation } from '../../hooks/mutations/useArchiveCustomSurveyTemplateMutation';
import { useUnarchiveCustomSurveyTemplateMutation } from '../../hooks/mutations/useUnarchiveCustomSurveyTemplateMutation';
import { TemplateScope, TemplateUsageInfo } from '../../api/customSurveyTemplates';
import { Routes } from '../../enums/Routes';
import { buildRoute } from '../../utils/routeBuilder';
import { categoriesOf } from '../CustomSurveyTemplatesPage/templatesView';
import { QuestionPreview } from './components/QuestionPreview';
import classes from './CustomSurveyTemplateDetailPage.module.css';

type LocationState = { copied?: boolean; created?: boolean; saved?: boolean; savedVersion?: number } | null;

/** «3 undersøkelser og 1 planlagt undersøkelse» */
const usageText = (usage: TemplateUsageInfo, t: TFunction) =>
  [
    usage.surveysCount > 0 && t('customSurveys.templateDetail.delete.surveys', { count: usage.surveysCount }),
    usage.scheduledSurveysCount > 0 &&
      t('customSurveys.templateDetail.delete.scheduled', { count: usage.scheduledSurveysCount }),
  ]
    .filter(Boolean)
    .join(t('customSurveys.templateDetail.delete.and'));

export const CustomSurveyTemplateDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId } = useParams<{ templateId: string }>();
  const id = templateId ? Number(templateId) : null;
  const state = location.state as LocationState;
  const copied = state?.copied === true;
  const created = state?.created === true;
  const saved = state?.saved === true;
  const savedVersion = state?.savedVersion;

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [blockedUsage, setBlockedUsage] = useState<TemplateUsageInfo | null>(null);
  const [actionFailed, setActionFailed] = useState(false);

  const { data: template, isLoading, error } = useGetCustomSurveyTemplateByIdQuery(id);
  const usage = useCheckCustomSurveyTemplateUsageQuery(id, { enabled: false });
  const onError = () => setActionFailed(true);

  const deleteMutation = useDeleteCustomSurveyTemplateMutation({
    onSuccess: () => navigate(Routes.CustomSurveyTemplates),
    onError,
  });
  const duplicateMutation = useDuplicateCustomSurveyTemplateMutation({
    onSuccess: (copy) =>
      navigate(buildRoute(Routes.CustomSurveyTemplateDetail, { templateId: copy.id }), { state: { copied: true } }),
    onError,
  });
  const archiveMutation = useArchiveCustomSurveyTemplateMutation({ onError });
  const unarchiveMutation = useUnarchiveCustomSurveyTemplateMutation({ onError });

  const breadcrumbs = (name?: string) => (
    <nav aria-label={t('breadcrumbs.ariaLabel')} className="ds-breadcrumbs">
      <ol>
        <li>
          <Link to={Routes.CustomSurveyTemplates} className="ds-link">
            {t('customSurveys.templateDetail.breadcrumb')}
          </Link>
        </li>
        {name && id !== null && (
          <li>
            <Link
              to={buildRoute(Routes.CustomSurveyTemplateDetail, { templateId: id })}
              className="ds-link"
              aria-current="page"
            >
              {name}
            </Link>
          </li>
        )}
      </ol>
    </nav>
  );

  if (isLoading) {
    return (
      <div className={classes.page}>
        <span className="ds-sr-only">{t('customSurveys.templateDetail.loading')}</span>
        <Skeleton variant="rectangle" height="120px" />
        <Skeleton variant="rectangle" height="96px" />
        <Skeleton variant="rectangle" height="320px" />
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className={classes.page}>
        {breadcrumbs()}
        <Alert data-color={error ? 'danger' : 'warning'}>
          <Heading level={2} data-size="xs">
            {t(error ? 'customSurveys.templateDetail.error.title' : 'customSurveys.templateDetail.notFound.title')}
          </Heading>
          <Paragraph>
            {t(error ? 'customSurveys.templateDetail.error.message' : 'customSurveys.templateDetail.notFound.message')}
          </Paragraph>
        </Alert>
      </div>
    );
  }

  const templateRef = template.id;
  const isTeam = template.scope === TemplateScope.TEAM;
  const surveys = template.surveysCreated ?? 0;
  const categories = categoriesOf(template);

  const startDelete = async () => {
    setActionFailed(false);
    setBlockedUsage(null);
    const result = await usage.refetch();
    if (result.error) {
      setActionFailed(true);
    } else if (result.data?.isInUse) {
      setBlockedUsage(result.data);
    } else {
      setConfirmDelete(true);
    }
  };

  const toggleArchive = () => {
    setActionFailed(false);
    setBlockedUsage(null);
    (template.isArchived ? unarchiveMutation : archiveMutation).mutate(templateRef);
  };

  return (
    <div className={classes.page}>
      {breadcrumbs(template.name)}

      {copied && (
        <Alert data-color="success" role="status">
          <Paragraph>{t('customSurveys.templateDetail.copied', { name: template.name })}</Paragraph>
        </Alert>
      )}
      {(created || saved || savedVersion !== undefined) && (
        <Alert data-color="success" role="status">
          <Paragraph>
            {savedVersion !== undefined
              ? t('customSurveys.templateDetail.savedVersion', { version: savedVersion })
              : t(created ? 'customSurveys.templateDetail.created' : 'customSurveys.templateDetail.saved')}
          </Paragraph>
        </Alert>
      )}
      {template.isArchived && (
        <Alert data-color="info">
          <Paragraph>{t('customSurveys.templateDetail.archivedNotice')}</Paragraph>
        </Alert>
      )}
      {actionFailed && (
        <Alert data-color="danger" role="alert">
          <Paragraph>{t('customSurveys.templateDetail.actionFailed')}</Paragraph>
        </Alert>
      )}

      <div className={classes.header}>
        <div className={classes.intro}>
          <span className={classes.eyebrow}>{t('customSurveys.templateDetail.eyebrow')}</span>
          <div className={classes.titleRow}>
            <Heading level={1} data-size="xl">
              {template.name}
            </Heading>
            <Tag data-size="sm" data-color="neutral" className={classes.versionTag}>
              {t('customSurveys.templateDetail.version', { version: template.version })}
            </Tag>
            {template.isArchived && (
              <Tag data-size="sm" data-color="warning">
                {t('customSurveys.templateDetail.archivedTag')}
              </Tag>
            )}
          </div>
          {template.description && <Paragraph>{template.description}</Paragraph>}
          {categories.length > 0 && (
            <ul className={classes.categories} aria-label={t('customSurveys.templateDetail.categories')}>
              {categories.map((category) => (
                <li key={category}>
                  <Tag data-size="sm" data-color="neutral">
                    {category}
                  </Tag>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className={classes.actions}>
          {!template.isArchived && (
            <Button asChild>
              <Link to={buildRoute(Routes.EditCustomSurveyTemplate, { templateId: templateRef })}>
                <PencilWritingIcon aria-hidden />
                {t('customSurveys.templateDetail.edit')}
              </Link>
            </Button>
          )}
          <Button
            variant="secondary"
            className={classes.whiteButton}
            loading={duplicateMutation.isPending}
            onClick={() => {
              setActionFailed(false);
              duplicateMutation.mutate(templateRef);
            }}
          >
            {!duplicateMutation.isPending && <FilesIcon aria-hidden />}
            {t('customSurveys.templateDetail.duplicate')}
          </Button>
        </div>
      </div>

      <dl className={classes.facts}>
        <div>
          <span aria-hidden="true" className={classes.factIcon}>
            <FileTextIcon />
          </span>
          <div>
            <dt>{t('customSurveys.templateDetail.facts.content')}</dt>
            <dd>{t('customSurveys.templateDetail.facts.questions', { count: template.questions.length })}</dd>
          </div>
        </div>
        <div>
          <span aria-hidden="true" className={classes.factIcon}>
            <ClockIcon />
          </span>
          <div>
            <dt>{t('customSurveys.templateDetail.facts.usedIn')}</dt>
            <dd>{t('customSurveys.templateDetail.facts.surveys', { count: surveys })}</dd>
          </div>
        </div>
        <div>
          <span aria-hidden="true" className={classes.factIcon}>
            {isTeam ? <PersonGroupIcon /> : <Buildings2Icon />}
          </span>
          <div>
            <dt>{t('customSurveys.templateDetail.facts.appliesTo')}</dt>
            <dd>
              {isTeam
                ? t('customSurveys.templateDetail.facts.team', { name: template.scopeTeamName ?? '' }).trim()
                : t('customSurveys.templateDetail.facts.company')}
            </dd>
          </div>
        </div>
      </dl>

      <section aria-labelledby="template-questions" className={classes.section}>
        <div className={classes.sectionIntro}>
          <Heading level={2} data-size="sm" id="template-questions">
            {t('customSurveys.templateDetail.questions.title')}
          </Heading>
          <Paragraph data-size="sm" className={classes.subtle}>
            {t('customSurveys.templateDetail.questions.description')}
          </Paragraph>
        </div>
        {template.questions.length === 0 ? (
          <Paragraph data-size="sm" className={classes.subtle}>
            {t('customSurveys.templateDetail.questions.none')}
          </Paragraph>
        ) : (
          <ol className={classes.questions}>
            {[...template.questions]
              .sort((a, b) => a.orderIndex - b.orderIndex)
              .map((question, index) => (
                <QuestionPreview key={question.id ?? index} question={question} number={index + 1} />
              ))}
          </ol>
        )}
      </section>

      <section aria-labelledby="template-manage" className={classes.manage}>
        <Heading level={2} data-size="2xs" id="template-manage">
          {t(
            template.isArchived
              ? 'customSurveys.templateDetail.manage.restoreTitle'
              : 'customSurveys.templateDetail.manage.title',
          )}
        </Heading>
        <Paragraph data-size="sm" className={classes.subtle}>
          {t(
            template.isArchived
              ? 'customSurveys.templateDetail.manage.restoreDescription'
              : 'customSurveys.templateDetail.manage.description',
          )}
        </Paragraph>
        <div className={classes.manageButtons}>
          <Button
            variant="secondary"
            className={classes.whiteButton}
            loading={archiveMutation.isPending || unarchiveMutation.isPending}
            onClick={toggleArchive}
          >
            {t(template.isArchived ? 'customSurveys.templateDetail.manage.restore' : 'customSurveys.templateDetail.manage.archive')}
          </Button>
          {!template.isArchived && (
            <Button variant="tertiary" data-color="danger" loading={usage.isFetching} onClick={startDelete}>
              {!usage.isFetching && <TrashIcon aria-hidden />}
              {t('customSurveys.templateDetail.manage.delete')}
            </Button>
          )}
        </div>
        {blockedUsage && (
          <Alert data-color="danger" role="alert">
            <div className={classes.alertContent}>
              <Heading level={3} data-size="2xs">
                {t('customSurveys.templateDetail.delete.blockedTitle')}
              </Heading>
              <Paragraph>
                {t('customSurveys.templateDetail.delete.blockedMessage', { usage: usageText(blockedUsage, t) })}
              </Paragraph>
              <Button variant="secondary" data-size="sm" onClick={() => setBlockedUsage(null)}>
                {t('customSurveys.templateDetail.delete.close')}
              </Button>
            </div>
          </Alert>
        )}
      </section>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        closedby="any"
        closeButton={t('customSurveys.templateDetail.delete.cancel')}
      >
        <div className={classes.dialogContent}>
          <Heading level={2} data-size="sm">
            {t('customSurveys.templateDetail.delete.confirmTitle', { name: template.name })}
          </Heading>
          <Paragraph>{t('customSurveys.templateDetail.delete.confirmMessage')}</Paragraph>
          <div className={classes.manageButtons}>
            <Button data-color="danger" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate(templateRef)}>
              {t('customSurveys.templateDetail.delete.confirm')}
            </Button>
            <Button variant="tertiary" onClick={() => setConfirmDelete(false)}>
              {t('customSurveys.templateDetail.delete.cancel')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
